/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align */

import {ProjectElement}         from './project.element';
import {ProjectElementType}     from './project.element.type';
import {InputsElement}          from './inputs.element';

import {
  Blob,
  BlobType
}                    from '../blob';

import {
  Project
}                    from '../project';

import { Logger }    from '../../util/logger';
import {first} from "rxjs/operators";
import {IProjectBulkCallBack} from "@/models/project/i.project.bulk.call.back";
import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";

/**
 *
 * @class DimensionElement
 *
 * @brief a dimension can contain 1..N blob inputs..but they all must be of the same
 * type.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
@ProjectElementFactoryDecorator('DimensionElement')
export class DimensionElement extends InputsElement {

  public dimActionKeys: string[] = ['renamedim', 'deletedim'];

  /* the kind of dimension these inputs are cast to */
  public dimKind: BlobType = BlobType.Scenario;

  /** standard constructor */
  constructor(
     project: Project,
          id: string,
        name: string,
        kind: BlobType,
      parent: ProjectElement,
    publish?: boolean
  ) {

    super(project, id, null, publish, ProjectElementType.Dimension);

    this.actionKeys       = this.actionKeys.concat(this.dimActionKeys);

    this.actionKeys       = this.actionKeys.filter(x => x !== 'dimensioninput');

    this._acceptsElements = [ProjectElementType.Blob];

    this._label           = name;
    this.dimKind          = kind;
    this._enabled         = true;
    this._icon            = 'blob-icons/' + Blob.iconOfType(this.dimKind);
    this._elementKind     = 'Dimension';

    /* link into hierarchy if needed */

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring DimensionElement");
  }

  /** fetch compatible blob type, for some elements this means what its cast to. */
  public blobType(): BlobType {

    return this.dimKind;
  }

  /**
   * sub-classes must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    const tmp = super.actions(actionSet);

    delete tmp.dimensioninput;

    tmp.renamedim = {
      name:     'Rename Dimension',
      icon:     'fas fa-tag',
      disabled:  this.isBusy()
    }

    tmp.deletedim = {
      name:     'Delete Dimension',
      icon:     'fas fa-trash-alt',
      disabled:  this.isBusy()
    };

    return tmp;
  }

  /** can this element be a drop site of drag and drop? */
  public acceptsDrop(data): boolean {

    return true;
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return this.doDrop(data, this);
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    if (this.isBusy()) {

      return false;
    }

    return true;
  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    /* anything in this dimension will be "cast" as the type of the dimension */
    return true;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({dimKind: this.dimKind});
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    super.fromNode(node);

    this.dimKind = node.dimKind;
    this._icon   = 'blob-icons/' + Blob.iconOfType(this.dimKind);
  }

  /** sub-classes must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    /* do any dimension specific stuff */

    switch (key) {

      case 'renamedim': {

        /* use a dialog to get the new name and then re-key any children... */

        const dim: DimensionElement = this;

        dim._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            Logger.debug('[DimensionElement] validating: ', name);

            /* is it a valid name? */

            if (name.trim() === '') {

              reject('Dimension name can not be empty.');

              return ;
            }

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = dim._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Dimension of that name already exists.');

                return ;
              }

            }

            resolve(name);

          },
          (name) => {

            /*
             * do the actual renaming...the dimension doesn't have a blob key, but child
             * blob keys might be dependant on the dimension name
             *
             */

            /* figure out what the path changes are ... */

            const       parents: ProjectElement[] = this.ancestors().reverse();
            parents.shift();
            const  search: string = dim._project.dirname() +
              '/' +
              parents.map(x => x._label).join('/') + `/${this._label}`;

            const replace: string = dim._project.dirname() +
              '/' +
              parents.map(x => x._label).join('/') + `/${name}`;

            Logger.debug(`[DimensionElement] renaming... ${search} => ${replace}...`);

            /* adjust (in bulk)... */

            this.shiftBlobs(search, replace, (result) => {

              if (result !== true) {

                /* there was a problem! */

                Logger.error(`[DimensionElement] could not properly rename children: `, result);

                return ;
              }

              Logger.debug('[DimensionElement] children re-named, saving...');

              dim._label = name;

              dim.redraw();

              dim.save();

            });


          }, {
            title: 'Rename Dimension',
            placeholder: this._label
          });

        return ;
      }

      case 'deletedim': {

        Logger.debug('[DimensionElement] deleting dim: ', this._label);

        this.deleteElements(selection);

        return ;
      }
    }

    if (this.actionKeys.find(x => x === key)) {

      /* let InputElement handle it */

      super.action(key, selection);

      return ;
    }

    /* Dimension actions */

    if (!this.dimActionKeys.find(x => x === key)) {

      /* unknown action */

      Logger.error('[DimensionElement] unknown action type: ', key);

      return ;
    }



  }



}


